import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { cn } from "../lib/utils";
import { getEntityIcon } from "../lib/templates";
import EntityFormModal from "./EntityFormModal";
import OnboardingModal from "./OnboardingModal";
import { Toaster } from "./ui";
import WorkspaceSwitcher from "./WorkspaceSwitcher";
import { HealthDot, healthSummary } from "./EntityHealth";
import FocusChip from "./FocusChip";
import {
  LayoutDashboard, Target, Users, FileText, Settings as SettingsIcon, Inbox,
  Sun, Moon, Globe, LogOut, Plus, Compass, CornerRightUp, ShieldCheck,
} from "lucide-react";

function NavItem({ to, icon: Icon, label, color, end, health, focus, badge }) {
  const { t } = useApp();
  return (
    <NavLink
      to={to}
      end={end}
      title={health ? healthSummary(health, t) : undefined}
      data-testid={`nav-${(to.replace(/^\//, "").replace(/\//g, "-")) || "home"}`}
      className={({ isActive }) =>
        cn(
          "group flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all",
          isActive
            ? "bg-surface-hover text-text-primary"
            : "text-text-secondary hover:text-text-primary hover:bg-surface-hover"
        )
      }
    >
      {health ? (
        <HealthDot status={health.status} />
      ) : color ? (
        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
      ) : (
        <Icon className="w-4 h-4 shrink-0" />
      )}
      <span className="truncate">{label}</span>
      {focus && <FocusChip focus={focus} compact />}
      {badge > 0 && (
        <span data-testid="nav-inbox-badge"
          className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-[10px] font-bold inline-flex items-center justify-center">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

export default function AppLayout({ children }) {
  const { t, theme, toggleTheme, lang, toggleLang, user, logout, entities, entityHealth, focusSummary, loadEntities, toast, wsScoped, wsRole, inboxUnread, highlightAddEntity, setHighlightAddEntity } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreate, setShowCreate] = useState(false);

  // Entity-scoped members have no access to group-wide views (Dashboard, OKR,
  // shared Contacts/Documents). If they land on one (e.g. after a workspace
  // switch or the "*" catch-all), send them to their first granted entity.
  const GATED_PATHS = ["/", "/okr", "/contacts", "/documents"];
  useEffect(() => {
    if (wsScoped && entities.length > 0 && GATED_PATHS.includes(location.pathname)) {
      navigate(`/entity/${entities[0].key}`, { replace: true });
    }
  }, [wsScoped, entities, location.pathname, navigate]);

  const onEntityCreated = async (ent, withChecklist) => {
    await loadEntities();
    if (!ent?.key) return;
    if (withChecklist) {
      navigate(`/entity/${ent.key}?tab=tasks`);
      toast(t("starter_checklist_created"));
    } else {
      navigate(`/entity/${ent.key}`);
    }
  };

  return (
    <div className="h-screen w-full flex bg-background overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r flex flex-col" style={{ background: "var(--surface)" }}>
        <div className="px-5 py-5 flex items-center gap-2.5 border-b">
          <div className="w-8 h-8 rounded-lg bg-text-primary text-background flex items-center justify-center font-heading font-black text-sm">
            CO
          </div>
          <div className="leading-tight">
            <div className="font-heading font-bold text-sm">{t("app_name")}</div>
            <div className="text-[9px] tracking-[0.22em] uppercase text-text-secondary">
              {t("app_tagline")}
            </div>
          </div>
        </div>

        <WorkspaceSwitcher />

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {!wsScoped && <NavItem to="/" icon={LayoutDashboard} label={t("nav_home")} end />}
          {!wsScoped && <NavItem to="/okr" icon={Target} label={t("nav_okr")} />}
          <NavItem to="/inbox" icon={Inbox} label={t("nav_inbox")} badge={inboxUnread?.total || 0} />
          {wsRole === "owner" && <NavItem to="/advisory" icon={Compass} label={t("nav_advisory")} />}

          <div className="pt-4 pb-1.5 px-3 flex items-center justify-between">
            <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-text-secondary">
              {t("nav_entities")}
            </span>
            {(wsRole === "owner" || wsRole === "admin") && (
              <button
                onClick={() => { setShowCreate(true); setHighlightAddEntity(false); }}
                data-testid="add-entity-button"
                title={t("new_entity")}
                className={cn(
                  "transition-colors p-0.5 rounded",
                  highlightAddEntity
                    ? "text-background bg-emerald-500 ring-4 ring-emerald-500/40 animate-pulse"
                    : "text-text-secondary hover:text-text-primary hover:bg-surface-hover"
                )}
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          {highlightAddEntity && entities.length === 0 && (
            <div className="mx-3 mb-1.5 flex items-center gap-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-1.5 text-[11px] text-emerald-500 animate-fade-in"
              data-testid="add-entity-hint">
              <CornerRightUp className="w-3.5 h-3.5 shrink-0" />
              <span className="leading-tight">{t("onb_start_here")}</span>
            </div>
          )}
          {entities.map((e) => (
            <NavItem
              key={e.id}
              to={`/entity/${e.key}`}
              icon={getEntityIcon(e.type)}
              label={e.name}
              color={e.color}
              health={entityHealth[e.id]}
              focus={focusSummary[e.id]}
            />
          ))}

          {!wsScoped && (
            <>
              <div className="pt-4 pb-1.5 px-3">
                <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-text-secondary">
                  {t("nav_shared")}
                </span>
              </div>
              <NavItem to="/contacts" icon={Users} label={t("nav_contacts")} />
              <NavItem to="/documents" icon={FileText} label={t("nav_documents")} />
            </>
          )}
          <div className="pt-4" />
          <NavItem to="/settings" icon={SettingsIcon} label={t("nav_settings")} />
          {user?.is_platform_admin && <NavItem to="/admin" icon={ShieldCheck} label={lang === "fr" ? "Admin" : "Admin"} />}
        </nav>

        {/* Footer controls */}
        <div className="px-3 py-3 border-t space-y-3">
          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
            >
              {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              {theme === "dark" ? t("light") : t("dark")}
            </button>
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
            >
              <Globe className="w-3.5 h-3.5" /> {lang.toUpperCase()}
            </button>
          </div>

          <div className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center text-xs font-semibold">
                {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-xs font-medium truncate">{user?.name || "—"}</div>
              <div className="text-[10px] text-text-secondary truncate">{user?.email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="logout-button"
              title={t("nav_logout")}
              className="text-text-secondary hover:text-red-400 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto" data-testid="main-content">
        {children ?? <Outlet context={{ navigate }} />}
      </main>

      <EntityFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onSaved={onEntityCreated}
        editing={null}
      />
      <OnboardingModal />
      <Toaster />
    </div>
  );
}
